/**
 * rule-form — OPEN-217 (DEC-382) the driver entry rule form, as pure functions.
 *
 * Bounds are typed in the unit the driver works in and stored canonical (the 0266 columns):
 * gauges in feet + inches → inches, driver time as H:MM → minutes, barrels and quantities as
 * decimals (up to 3 places, numeric(12,3)), tank counts as whole numbers. Every conversion is
 * exact on the typed text; nothing goes through float multiplication of a parsed decimal.
 */

import {
  VALIDATION_RULE_FIELD_CODES,
  VALIDATION_RULE_FIELDS,
  VALIDATION_RULE_MESSAGE_MAX,
  formatValidationMessage,
  formatValidationValue,
  serviceChargeRuleStage,
  validationFieldApplies,
  validationRequiredOffered,
  validationRuleShapeError,
  type ValidationRuleCheckKind,
  type ValidationRuleField,
  type ValidationRuleSeverity,
  type ValidationRuleUnit,
  type ValidationShape,
} from '@lodex-pro/shared/validation-rules';
import type { RuleBody } from './use-validation-rules';

/** A bound as typed. `main` is the number (feet for a gauge, H:MM for time); `inches` gauges only. */
export interface BoundInput {
  readonly main: string;
  readonly inches: string;
}
export const EMPTY_BOUND: BoundInput = { main: '', inches: '' };

export interface RuleForm {
  readonly field: ValidationRuleField;
  readonly checkKind: ValidationRuleCheckKind;
  readonly min: BoundInput;
  readonly max: BoundInput;
  readonly message: string;
  readonly severity: ValidationRuleSeverity;
  readonly serviceChargeId: string;
}

export const CHECK_LABELS: Record<ValidationRuleCheckKind, string> = {
  GT: 'More than',
  LT: 'Less than',
  BETWEEN: 'Outside a range',
  REQUIRED: 'Left empty',
};

/** The fields a driver can enter on this job type, in catalogue order. */
export function fieldsFor(shape: ValidationShape): ValidationRuleField[] {
  return VALIDATION_RULE_FIELD_CODES.filter((f) => validationFieldApplies(f, shape));
}

/** REQUIRED only where the job type leaves the field optional (and the field allows it). */
export function checksFor(
  field: ValidationRuleField,
  shape: ValidationShape,
): ValidationRuleCheckKind[] {
  const base: ValidationRuleCheckKind[] = ['GT', 'LT', 'BETWEEN'];
  return validationRequiredOffered(field, shape) ? [...base, 'REQUIRED'] : base;
}

/** An extra service a rule may name: active, and not a disposal-screen one on an ON_SITE job. */
export function serviceChargeFits(
  shape: ValidationShape,
  charge: { readonly isActive: boolean; readonly stage: string },
): boolean {
  if (!charge.isActive) return false;
  return !(shape.completionLocation === 'ON_SITE' && charge.stage === 'DISPOSAL');
}

/** Where the driver meets the rule, for the form's hint. */
export function stageHint(field: ValidationRuleField, serviceChargeStage?: string | null): string {
  const stage =
    field === 'SERVICE_QTY'
      ? serviceChargeRuleStage(serviceChargeStage)
      : VALIDATION_RULE_FIELDS[field].stage;
  if (stage === 'TANK') return 'Checked as the driver saves each tank.';
  if (stage === 'CAPTURE') return 'Checked when the driver finishes the run ticket.';
  if (stage === 'DISPOSAL') return 'Checked when the driver completes the disposal leg.';
  return 'Checked on the screen where this extra service is entered.';
}

export function defaultMessage(severity: ValidationRuleSeverity): string {
  return severity === 'BLOCK'
    ? 'You entered {value}. That can’t be right; please check it.'
    : 'You entered {value}. Are you sure?';
}

const DECIMAL_RE = /^-?\d+(\.\d{1,3})?$/;
const WHOLE_RE = /^\d+$/;
const HM_RE = /^(\d+):([0-5]\d)$/;

/** A typed decimal as an exact number with at most 3 places (the column's scale). */
function exactDecimal(text: string): number | null {
  const t = text.trim();
  if (!DECIMAL_RE.test(t)) return null;
  return Number(t);
}

/**
 * A typed bound in canonical units, or null when it doesn't parse. Inches may carry up to 3
 * decimals and must be under 12; feet and inches are whole-and-fraction text added exactly
 * (feet × 12 is an integer, so the sum keeps the inches' precision).
 */
export function parseBound(unit: ValidationRuleUnit, input: BoundInput): number | null {
  switch (unit) {
    case 'IN': {
      const ftText = input.main.trim() === '' ? '0' : input.main.trim();
      const inText = input.inches.trim() === '' ? '0' : input.inches.trim();
      if (input.main.trim() === '' && input.inches.trim() === '') return null;
      if (!WHOLE_RE.test(ftText)) return null;
      const inches = exactDecimal(inText);
      if (inches === null || inches < 0 || inches >= 12) return null;
      return Number(ftText) * 12 + inches;
    }
    case 'MIN': {
      const t = input.main.trim();
      const hm = HM_RE.exec(t);
      if (hm !== null) return Number(hm[1]) * 60 + Number(hm[2]);
      // Whole hours alone ("16") are accepted too.
      return WHOLE_RE.test(t) ? Number(t) * 60 : null;
    }
    case 'COUNT': {
      const t = input.main.trim();
      return WHOLE_RE.test(t) ? Number(t) : null;
    }
    case 'BBL':
    case 'QTY':
      return exactDecimal(input.main);
  }
}

/** A stored bound back into the form's text. */
export function boundToInput(unit: ValidationRuleUnit, value: number | null): BoundInput {
  if (value === null) return EMPTY_BOUND;
  switch (unit) {
    case 'IN': {
      const feet = Math.floor(value / 12);
      // Back to at most 3 places, as stored.
      const inches = Math.round((value - feet * 12) * 1000) / 1000;
      return { main: String(feet), inches: String(inches) };
    }
    case 'MIN': {
      const total = Math.round(value);
      const h = Math.floor(total / 60);
      return { main: `${String(h)}:${String(total - h * 60).padStart(2, '0')}`, inches: '' };
    }
    case 'COUNT':
    case 'BBL':
    case 'QTY':
      return { main: String(value), inches: '' };
  }
}

/** A form for a new rule on the first field this job type allows. */
export function newRuleForm(shape: ValidationShape): RuleForm {
  const field = fieldsFor(shape)[0] ?? 'TANK_WATER_BBL';
  return {
    field,
    checkKind: 'GT',
    min: EMPTY_BOUND,
    max: EMPTY_BOUND,
    message: defaultMessage('WARNING'),
    severity: 'WARNING',
    serviceChargeId: '',
  };
}

export function formFromRule(rule: {
  readonly field: ValidationRuleField;
  readonly checkKind: ValidationRuleCheckKind;
  readonly minValue: number | null;
  readonly maxValue: number | null;
  readonly message: string;
  readonly severity: ValidationRuleSeverity;
  readonly serviceChargeId: string | null;
}): RuleForm {
  const unit = VALIDATION_RULE_FIELDS[rule.field].unit;
  return {
    field: rule.field,
    checkKind: rule.checkKind,
    min: boundToInput(unit, rule.minValue),
    max: boundToInput(unit, rule.maxValue),
    message: rule.message,
    severity: rule.severity,
    serviceChargeId: rule.serviceChargeId ?? '',
  };
}

/** Which bound inputs the check shows: GT a minimum, LT a maximum, BETWEEN both. */
export function boundsShown(check: ValidationRuleCheckKind): { min: boolean; max: boolean } {
  return { min: check === 'GT' || check === 'BETWEEN', max: check === 'LT' || check === 'BETWEEN' };
}

export type BuildResult =
  | { readonly ok: true; readonly body: RuleBody }
  | { readonly ok: false; readonly error: string };

/** The form as an API body, or the first thing wrong with it in plain words. */
export function buildRuleBody(form: RuleForm): BuildResult {
  const unit = VALIDATION_RULE_FIELDS[form.field].unit;
  const shown = boundsShown(form.checkKind);
  const min = shown.min ? parseBound(unit, form.min) : null;
  const max = shown.max ? parseBound(unit, form.max) : null;
  const what =
    unit === 'IN'
      ? 'feet and inches (inches under 12)'
      : unit === 'MIN'
        ? 'hours and minutes, like 16:00'
        : unit === 'COUNT'
          ? 'a whole number'
          : 'a number with up to 3 decimals';
  if (shown.min && min === null) return { ok: false, error: `Enter the lower limit as ${what}.` };
  if (shown.max && max === null) return { ok: false, error: `Enter the upper limit as ${what}.` };
  if (form.checkKind === 'BETWEEN' && min !== null && max !== null && min > max) {
    return { ok: false, error: 'The lower limit must not be more than the upper limit.' };
  }
  const message = form.message.trim();
  if (message === '' || message.length > VALIDATION_RULE_MESSAGE_MAX) {
    return {
      ok: false,
      error: `Enter the message the driver sees (up to ${String(VALIDATION_RULE_MESSAGE_MAX)} characters).`,
    };
  }
  const serviceChargeId = form.field === 'SERVICE_QTY' ? form.serviceChargeId : null;
  if (form.field === 'SERVICE_QTY' && serviceChargeId === '') {
    return { ok: false, error: 'Choose the extra service this rule checks.' };
  }
  const body: RuleBody = {
    field: form.field,
    checkKind: form.checkKind,
    minValue: min,
    maxValue: max,
    message,
    severity: form.severity,
    serviceChargeId,
  };
  if (validationRuleShapeError(body) !== null) {
    return { ok: false, error: 'Check the limits for this kind of check.' };
  }
  return { ok: true, body };
}

/** A sample value that breaks the rule, for the preview: the bound itself or one unit past. */
export function sampleValue(form: RuleForm): number | null {
  if (form.checkKind === 'REQUIRED') return null;
  const unit = VALIDATION_RULE_FIELDS[form.field].unit;
  const step = unit === 'IN' ? 12 : unit === 'MIN' ? 60 : 1;
  const min = parseBound(unit, form.min);
  const max = parseBound(unit, form.max);
  if (form.checkKind === 'LT') return max === null ? null : Math.max(0, max - step);
  if (form.checkKind === 'BETWEEN') return max === null ? null : max + step;
  return min === null ? null : min + step;
}

/** "Driver sees: …" — the message with a sample value, or null until a value is typed. */
export function previewMessage(form: RuleForm): string | null {
  const v = sampleValue(form);
  if (form.checkKind !== 'REQUIRED' && v === null) return null;
  return formatValidationMessage(form.message, form.field, v);
}

/** "More than 8 ft 4 in" — a stored rule's check in words. */
export function describeCheck(rule: {
  readonly field: ValidationRuleField;
  readonly checkKind: ValidationRuleCheckKind;
  readonly minValue: number | null;
  readonly maxValue: number | null;
}): string {
  const fmt = (v: number | null): string =>
    v === null ? '?' : formatValidationValue(rule.field, v);
  switch (rule.checkKind) {
    case 'GT':
      return `More than ${fmt(rule.minValue)}`;
    case 'LT':
      return `Less than ${fmt(rule.maxValue)}`;
    case 'BETWEEN':
      return `Outside ${fmt(rule.minValue)} to ${fmt(rule.maxValue)}`;
    case 'REQUIRED':
      return 'Left empty';
  }
}

/** "Single gauge · Disposal facility" for a family's badge. */
export function shapeLabel(shape: ValidationShape): string {
  const method =
    shape.captureMethod === 'BEFORE_AFTER'
      ? 'Before / after'
      : shape.captureMethod === 'LINE_ITEM'
        ? 'Line items'
        : 'Single gauge';
  const where =
    shape.completionLocation === 'ON_SITE'
      ? 'On site'
      : shape.completionLocation === 'OTHER'
        ? 'Other destination'
        : 'Disposal facility';
  return `${method} · ${where}`;
}
